import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    getMaintenanceMonthlyCostDetails,
    updateMaintenanceMonthlyCost,
} from '../../services/MaintenanceServices';
import { ALLOCATION_METHODS, COST_TYPES } from './maintenanceConstants';

function MaintenanceMonthlyCostEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [inputs, setInputs] = useState({
        plant_id: '',
        cost_year: '',
        cost_month: '',
        cost_type: 'EB',
        bill_amount: 0,
        bill_kwh: 0,
        allocation_method: 'BY_KWH',
        user_remarks: '',
    });

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status)
                .map((p) => ({ value: p.id, label: p.plant_alias })));
        });
    }, [cookies]);

    useEffect(() => {
        if (!id) return;
        setInProgress(true);
        getMaintenanceMonthlyCostDetails(cookies, id)
            .then((data) => {
                setIsActive(data.status === true || data.status === 1 || data.status === 'Active');
                setInputs({
                    plant_id: data.plant_id,
                    cost_year: data.cost_year,
                    cost_month: data.cost_month,
                    cost_type: data.cost_type,
                    bill_amount: data.bill_amount,
                    bill_kwh: data.bill_kwh,
                    allocation_method: data.allocation_method,
                    user_remarks: data.user_remarks || '',
                });
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!isActive) return;
        setInProgress(true);
        updateMaintenanceMonthlyCost(cookies, id, {
            ...inputs,
            cost_year: Number(inputs.cost_year),
            cost_month: Number(inputs.cost_month),
            bill_amount: Number(inputs.bill_amount),
            bill_kwh: Number(inputs.bill_kwh),
        })
            .then(() => Swal.fire({ icon: 'success', title: 'Month-end cost updated' }))
            .catch((error) => displayError(error?.response?.data || error, 'Update Failed'))
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls
                        tableLink="/MaintenanceMonthlyCostTable"
                        formLink="/MaintenanceMonthlyCost"
                        addNewLabel="Main Form"
                        enableCancel
                        onCancel={() => navigate('/MaintenanceMonthlyCostTable')}
                    />
                    <FormPageLayout
                        moduleLabel="MAINTENANCE"
                        title="Month-End Costs"
                        subtitle="Update EB bill, pump cost and allocation method."
                        modeLabel={isActive ? 'UPDATE MODE' : 'CANCELLED'}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                                        <label className="form-group col-sm-4 text-right">Plant</label>
                                        <select name="plant_id" className="form-control col-sm-7" value={inputs.plant_id} disabled>
                                            <option value="">Select Plant</option>
                                            {plants.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
                                        </select>

                                        <label className="form-group col-sm-4 text-right">Year</label>
                                        <input type="number" name="cost_year" className="form-control col-sm-7" value={inputs.cost_year} onChange={handleChange} readOnly={!isActive} />

                                        <label className="form-group col-sm-4 text-right">Month</label>
                                        <input type="number" min="1" max="12" name="cost_month" className="form-control col-sm-7" value={inputs.cost_month} onChange={handleChange} readOnly={!isActive} />

                                        <label className="form-group col-sm-4 text-right">Cost Type</label>
                                        <select name="cost_type" className="form-control col-sm-7" value={inputs.cost_type} onChange={handleChange} disabled={!isActive}>
                                            {COST_TYPES.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                                        </select>

                                        <label className="form-group col-sm-4 text-right">Bill Amount ₹</label>
                                        <input type="number" step="0.01" name="bill_amount" className="form-control col-sm-7" value={inputs.bill_amount} onChange={handleChange} readOnly={!isActive} />

                                        <label className="form-group col-sm-4 text-right">Bill kWh</label>
                                        <input type="number" step="0.001" name="bill_kwh" className="form-control col-sm-7" value={inputs.bill_kwh} onChange={handleChange} readOnly={!isActive} />

                                        <label className="form-group col-sm-4 text-right">Allocation</label>
                                        <select name="allocation_method" className="form-control col-sm-7" value={inputs.allocation_method} onChange={handleChange} disabled={!isActive}>
                                            {ALLOCATION_METHODS.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                                        </select>

                                        <label className="form-group col-sm-4 text-right">Remarks</label>
                                        <input name="user_remarks" className="form-control col-sm-7" value={inputs.user_remarks} onChange={handleChange} readOnly={!isActive} />
                                    </div>
                                    {isActive && <Button color="primary" type="submit">Update Month-End Cost</Button>}
                                </Col>
                            </Row>
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default MaintenanceMonthlyCostEdit;
